import { useState } from 'react'
import { Plus, Edit2, Trash2, X, Archive, Calendar, CheckCircle } from 'lucide-react'

export default function DataRetentionPoliciesPage() {
  const [policies, setPolicies] = useState([
    {
      id: 1,
      name: 'Email Retention Policy',
      dataType: 'emails',
      retentionPeriod: 7,
      autoDelete: true,
      archiveOption: true,
      created: new Date().toLocaleDateString()
    },
    {
      id: 2,
      name: 'Log File Retention',
      dataType: 'logs',
      retentionPeriod: 90,
      autoDelete: true,
      archiveOption: false,
      created: new Date().toLocaleDateString()
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    dataType: 'emails',
    retentionPeriod: 30,
    autoDelete: true,
    archiveOption: true
  })

  const handleCreateClick = () => {
    setEditingPolicy(null)
    setFormData({
      name: '',
      dataType: 'emails',
      retentionPeriod: 30,
      autoDelete: true,
      archiveOption: true
    })
    setShowModal(true)
  }

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy)
    setFormData({
      name: policy.name,
      dataType: policy.dataType,
      retentionPeriod: policy.retentionPeriod,
      autoDelete: policy.autoDelete,
      archiveOption: policy.archiveOption
    })
    setShowModal(true)
  }

  const handleSavePolicy = () => {
    if (!formData.name) {
      alert('Please fill in all required fields')
      return
    }

    if (editingPolicy) {
      setPolicies(policies.map(p =>
        p.id === editingPolicy.id
          ? { ...p, ...formData }
          : p
      ))
    } else {
      const newPolicy = {
        id: Date.now(),
        ...formData,
        created: new Date().toLocaleDateString()
      }
      setPolicies([...policies, newPolicy])
    }

    setShowModal(false)
  }

  const handleDeletePolicy = (id) => {
    if (confirm('Are you sure you want to delete this policy?')) {
      setPolicies(policies.filter(p => p.id !== id))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Archive className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Data Retention Policies</h1>
          </div>
          <p className="text-slate-400">Define retention schedules for different data types and ensure compliance</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Policies</div>
            <div className="text-3xl font-bold text-blue-400">{policies.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Auto-Delete Enabled</div>
            <div className="text-3xl font-bold text-green-400">{policies.filter(p => p.autoDelete).length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">With Archive</div>
            <div className="text-3xl font-bold text-cyan-400">{policies.filter(p => p.archiveOption).length}</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create Retention Policy
        </button>

        {/* Policies List */}
        <div className="space-y-4">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{policy.name}</h3>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold text-blue-400 bg-blue-600/20">
                      {policy.dataType.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-slate-500">Retention Period:</span>
                      <span className="text-white ml-2">{policy.retentionPeriod} days</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Auto-Delete:</span>
                      <span className={`text-white ml-2 ${policy.autoDelete ? 'text-green-400' : 'text-red-400'}`}>
                        {policy.autoDelete ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Archive Option:</span>
                      <span className={`text-white ml-2 ${policy.archiveOption ? 'text-green-400' : 'text-red-400'}`}>
                        {policy.archiveOption ? 'Available' : 'None'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Created:</span>
                      <span className="text-white ml-2">{policy.created}</span>
                    </div>
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <p className="text-slate-400 text-xs">
                      Data will be {policy.autoDelete ? 'automatically deleted' : 'marked for review'} after {policy.retentionPeriod} days.
                      {policy.archiveOption && ' Data can be archived before deletion.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditPolicy(policy)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePolicy(policy.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPolicy ? 'Edit Retention Policy' : 'Create Retention Policy'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Policy Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Email Retention Policy"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Data Type</label>
                    <select
                      value={formData.dataType}
                      onChange={(e) => setFormData({ ...formData, dataType: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="emails">Emails</option>
                      <option value="logs">Logs</option>
                      <option value="documents">Documents</option>
                      <option value="databases">Databases</option>
                      <option value="archives">Archives</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Retention Period (Days)</label>
                    <input
                      type="number"
                      value={formData.retentionPeriod}
                      onChange={(e) => setFormData({ ...formData, retentionPeriod: parseInt(e.target.value) })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      min="1"
                      max="3650"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.autoDelete}
                      onChange={(e) => setFormData({ ...formData, autoDelete: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 text-blue-600 bg-slate-700 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-white font-semibold">Automatically delete data after retention period</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.archiveOption}
                      onChange={(e) => setFormData({ ...formData, archiveOption: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 text-blue-600 bg-slate-700 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-white font-semibold">Allow archiving before deletion</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePolicy}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Policy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
